import { useFlag } from '@reflag/react-sdk';
import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenWelcome } from '@gitbook/app-navigation';
import { GitBookLoader } from '@gitbook/spine-react';

import { type NavigationTarget, useNavigation } from '../../navigation';
import {
    apiQuery,
    CurrentOrganizationProvider,
    checkIsSiteStructureEmpty,
    useCurrentOrganization,
} from '../../state';
import { SiteOnboardingScreen } from '../Onboarding';
import { SiteLoadingProvider } from '../Site/components';
import { FLOW_STEPS, useFlowContext } from './useFlowContext';
import { WelcomeLayout } from './WelcomeLayout';
import { default as WelcomeScreen } from './WelcomeScreen';

/**
 * First screen as part of the user onboarding.
 * Also used when creating an organization.
 */
export default function OnboardingFlow(props: {
    screen: NavigationScreenWelcome;
}): React.ReactElement | null {
    const { screen } = props;
    const [state, setStep] = useFlowContext();
    const onboardingFlag = useFlag('ONBOARDING');

    let redirectTo: NavigationTarget | null = null;
    let title: string | undefined;
    let content: React.ReactNode = null;

    switch (state.step) {
        case FLOW_STEPS.CREATE_ORGANIZATION:
            if (screen.organization) {
                if (onboardingFlag.isLoading) {
                    content = <WelcomeLoader />;
                    break;
                }

                if (!onboardingFlag.isEnabled) {
                    redirectTo = {
                        screen: {
                            type: 'root',
                            organization: screen.organization,
                        },
                    };
                    content = <WelcomeLoader />;
                    break;
                }

                content = <OrganizationWelcomeResolver />;
                break;
            }

            content = (
                <WelcomeScreen
                    screen={screen}
                    onNext={({ organization, site, hasBootstrappedContent }) => {
                        setStep({
                            step: FLOW_STEPS.COMPLETE,
                            context: { organization, site, hasBootstrappedContent },
                        });
                    }}
                />
            );
            break;

        case FLOW_STEPS.COMPLETE:
            assertOrgAndSiteCreated(state.context);
            title = state.context.site.title;

            if (!state.context.hasBootstrappedContent) {
                if (screen.organization !== state.context.organization.id) {
                    redirectTo = {
                        screen: {
                            type: 'welcome',
                            organization: state.context.organization.id,
                        },
                    };
                    content = <WelcomeLoader />;
                    break;
                }

                if (onboardingFlag.isLoading) {
                    content = <WelcomeLoader />;
                    break;
                }

                if (!onboardingFlag.isEnabled) {
                    redirectTo = {
                        screen: {
                            type: 'site',
                            organization: state.context.organization.id,
                            site: state.context.site.id,
                        },
                    };
                    content = <WelcomeLoader />;
                    break;
                }

                content = (
                    <CurrentOrganizationProvider organization={state.context.organization}>
                        <SiteLoadingProvider>
                            <SiteOnboardingScreen
                                site={state.context.site}
                                organization={state.context.organization}
                            />
                        </SiteLoadingProvider>
                    </CurrentOrganizationProvider>
                );
                break;
            }

            redirectTo = {
                screen: {
                    type: 'site',
                    organization: state.context.organization.id,
                    site: state.context.site.id,
                },
            };
            content = <WelcomeLoader />;
            break;

        default:
            return null;
    }

    return (
        <WelcomeLayout title={title}>
            <WelcomeRedirect to={redirectTo} />
            <React.Suspense fallback={<WelcomeLoader />}>{content}</React.Suspense>
        </WelcomeLayout>
    );
}

function assertOrgAndSiteCreated({
    organization,
    site,
}: {
    organization: api.Organization;
    site: api.Site;
}) {
    if (!organization) {
        throw new Error('Could not create organization');
    }

    if (!site) {
        throw new Error('Site must be created');
    }
}

function OrganizationWelcomeResolver() {
    const organization = useCurrentOrganization();

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery(
            'getOrganizationAllContent',
            organization.permissions.admin ? { organizationId: organization.id } : skipToken
        )
    );

    if (!organization.permissions.admin) {
        return <WelcomeRedirect to={{ screen: { type: 'home', organization: organization.id } }} />;
    }

    if (!organizationContent || organizationContent.sites.length !== 1) {
        return <WelcomeRedirect to={{ screen: { type: 'home', organization: organization.id } }} />;
    }

    const [siteContent] = organizationContent.sites;
    if (!checkIsSiteStructureEmpty(siteContent.structure)) {
        return <WelcomeRedirect to={{ screen: { type: 'home', organization: organization.id } }} />;
    }

    return (
        <SiteLoadingProvider>
            <SiteOnboardingScreen site={siteContent.site} organization={organization} />
        </SiteLoadingProvider>
    );
}

function WelcomeRedirect(props: { to: NavigationTarget | null }) {
    const { to } = props;
    const { navigate } = useNavigation();
    React.useLayoutEffect(() => {
        if (to) {
            navigate(to, { replace: true });
        }
    }, [navigate, to]);

    return null;
}

function WelcomeLoader() {
    return (
        <div className="flex min-h-80 w-full flex-col items-center justify-center gap-4">
            <GitBookLoader />
            <div className="text-ui-heading-small">Preparing your workspace...</div>
        </div>
    );
}
